import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Volume2, 
  Volume1,
  VolumeX, 
  Play, 
  SkipForward,
  Maximize,
  Minimize,
  Flame,
  Sparkles,
  Trophy,
  Clock,
  RotateCcw
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { Button } from '../ui';

// Helper to determine the letter for a given number
export const getBingoLetter = (num: number): 'B' | 'I' | 'N' | 'G' | 'O' | '' => {
  if (num >= 1 && num <= 15) return 'B';
  if (num >= 16 && num <= 30) return 'I';
  if (num >= 31 && num <= 45) return 'N';
  if (num >= 46 && num <= 60) return 'G';
  if (num >= 61 && num <= 75) return 'O';
  return '';
};

// Column theme colors & styling
export const BALL_THEMES = {
  B: {
    letter: 'B',
    label: 'B (1 - 15)',
    gradient: 'from-blue-400 via-blue-600 to-indigo-900',
    shadow: 'shadow-[0_0_35px_rgba(59,130,246,0.6)]',
    badgeBg: 'bg-gradient-to-r from-blue-600 to-indigo-700 text-white border-2 border-blue-400 shadow-[0_0_15px_rgba(59,130,246,0.5)]',
    text: 'text-blue-600',
    calledBg: 'bg-blue-600 text-white border-2 border-blue-300 shadow-[0_0_14px_rgba(59,130,246,0.7)]',
    lastCalledBg: 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-yellow-500 text-black ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(250,204,21,1)] scale-110 z-20 font-black'
  },
  I: {
    letter: 'I',
    label: 'I (16 - 30)',
    gradient: 'from-rose-400 via-rose-600 to-red-950',
    shadow: 'shadow-[0_0_35px_rgba(244,63,94,0.6)]',
    badgeBg: 'bg-gradient-to-r from-rose-600 to-red-700 text-white border-2 border-rose-400 shadow-[0_0_15px_rgba(244,63,94,0.5)]',
    text: 'text-rose-600',
    calledBg: 'bg-rose-600 text-white border-2 border-rose-300 shadow-[0_0_14px_rgba(244,63,94,0.7)]',
    lastCalledBg: 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-yellow-500 text-black ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(250,204,21,1)] scale-110 z-20 font-black'
  },
  N: {
    letter: 'N',
    label: 'N (31 - 45)',
    gradient: 'from-amber-300 via-amber-500 to-orange-900',
    shadow: 'shadow-[0_0_35px_rgba(245,158,11,0.6)]',
    badgeBg: 'bg-gradient-to-r from-amber-500 to-orange-600 text-black border-2 border-amber-300 shadow-[0_0_15px_rgba(245,158,11,0.5)]',
    text: 'text-amber-600',
    calledBg: 'bg-amber-500 text-black border-2 border-amber-200 shadow-[0_0_14px_rgba(245,158,11,0.7)] font-black',
    lastCalledBg: 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-yellow-500 text-black ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(250,204,21,1)] scale-110 z-20 font-black'
  },
  G: {
    letter: 'G',
    label: 'G (46 - 60)',
    gradient: 'from-emerald-300 via-emerald-600 to-teal-950',
    shadow: 'shadow-[0_0_35px_rgba(16,185,129,0.6)]',
    badgeBg: 'bg-gradient-to-r from-emerald-600 to-teal-700 text-white border-2 border-emerald-400 shadow-[0_0_15px_rgba(16,185,129,0.5)]',
    text: 'text-emerald-600',
    calledBg: 'bg-emerald-600 text-white border-2 border-emerald-300 shadow-[0_0_14px_rgba(16,185,129,0.7)]',
    lastCalledBg: 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-yellow-500 text-black ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(250,204,21,1)] scale-110 z-20 font-black'
  },
  O: {
    letter: 'O',
    label: 'O (61 - 75)',
    gradient: 'from-purple-400 via-purple-600 to-indigo-950',
    shadow: 'shadow-[0_0_35px_rgba(168,85,247,0.6)]',
    badgeBg: 'bg-gradient-to-r from-purple-600 to-indigo-800 text-white border-2 border-purple-400 shadow-[0_0_15px_rgba(168,85,247,0.5)]',
    text: 'text-purple-600',
    calledBg: 'bg-purple-600 text-white border-2 border-purple-300 shadow-[0_0_14px_rgba(168,85,247,0.7)]',
    lastCalledBg: 'bg-gradient-to-tr from-amber-400 via-yellow-300 to-yellow-500 text-black ring-4 ring-yellow-400 shadow-[0_0_30px_rgba(250,204,21,1)] scale-110 z-20 font-black'
  }
};

// Sassy & Quirky caller quips dictionary
export const SASSY_NUMBER_QUOTES: Record<number, string> = {
  1: "Kelly's eye — or the number of emails you're ignoring right now.",
  2: "One little duck — looking for a pond and someone with actual luck.",
  3: "Cup of tea! Take a sip, because this card isn't winning itself.",
  4: "B4, like I loved my spouse before I met you!",
  5: "Man alive, five! Keep the energy up, people!",
  6: "Half a dozen — about how many times you've checked the wrong line.",
  7: "Lucky seven — don't get cocky, it's just one number.",
  8: "Garden gate — keep your eyes on your own card, Gladys!",
  9: "Doctor's orders, nine! Take two deep breaths.",
  10: "Big fat hen — or Uncle Bob after Thanksgiving dinner.",
  11: "Legs eleven — looking good, darling, now dab that card!",
  12: "One dozen — hope your luck is fresher than gas station sushi.",
  13: "Unlucky for some, hilarious for the rest of us!",
  14: "Valentine's Day — roses are red, your dabber is ready.",
  15: "Young and keen, fifteen! Eyes on the prize.",
  16: "Sweet sixteen — and never been caught bending the rules.",
  17: "Dancing queen, seventeen! You can dance if you get a line.",
  18: "Coming of age, eighteen! Adults only in the winner's circle.",
  20: "One score — more points than your favorite sports team scored.",
  21: "Key of the door, twenty-one! Time to unlock a win.",
  22: "Two little ducks! Quack quack, keep up folks!",
  25: "Duck and dive, twenty-five! Stay alive and dab it down.",
  28: "Over weight, twenty-eight! Put down the snack bowl, pick up the pen.",
  30: "Dirty thirty, flirty thirty! Who needs a curfew anyway?",
  33: "All the threes, dirty knee! Keep 'em crossed for a win.",
  38: "Christmas cake, thirty-eight! Sweeter than revenge.",
  40: "Life begins at forty! Or at least your first decent line of bingo.",
  44: "Droopy drawers! Pull 'em up, we've got a game to win.",
  45: "Halfway there! Whoa-oh, living on a prayer!",
  46: "Up to tricks, forty-six! No funny business in the back row.",
  50: "Half a century, fifty! Fifty and still fabulous.",
  55: "Snakes alive — double five! Sss-stop complaining and mark your square.",
  57: "Heinz fifty-seven — 57 varieties of nail-biting tension.",
  60: "Five dozen, sixty! Check those four corners!",
  62: "Tickety-boo, sixty-two! Doing just fine.",
  66: "Clickety click, sixty-six! Almost at the finish line.",
  69: "Either way up, sixty-nine! Keep your minds in the game, folks.",
  70: "Three score and ten, seventy! The pressure is building.",
  72: "Six dozen, seventy-two! Somebody has to be close!",
  75: "Top of the shop, seventy-five! That's the end of the line, folks!"
};

export const GENERAL_SASSY_QUOTES = [
  "Yes Brenda, this applies to your card too!",
  "Don't just stare at it, dab it!",
  "I can practically smell the adrenaline in this room.",
  "Someone's one away, I can feel the glares.",
  "If this just gave you bingo, yell it loud so the neighbors hear!",
  "No refunds on dried-out dabbers!",
  "Hold onto your lucky charms, folks!",
  "Look alive, glory awaits the fastest dabber!",
  "Don't blame the caller, blame the algorithm!",
  "Check your diagonals, you might already have it!",
  "If you missed this one, you owe the table donuts.",
  "Clear your throat and get ready to shout!",
  "Your lucky number has arrived... hopefully.",
  "Don't look at me like that, I don't pick the balls!"
];

export const getSassyQuote = (num: number): string => {
  if (SASSY_NUMBER_QUOTES[num]) {
    return SASSY_NUMBER_QUOTES[num];
  }
  const index = Math.abs(num * 7) % GENERAL_SASSY_QUOTES.length;
  return GENERAL_SASSY_QUOTES[index];
};

// Web Audio API Sound Synthesizers
const getAudioContext = (): AudioContext | null => {
  if (typeof window === 'undefined') return null;
  const AudioCtx = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
  if (!AudioCtx) return null;
  return new AudioCtx();
};

// Realistic Tumbling Cage Sound (metallic rattle + ball collisions)
const playCageTumblerSound = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const ticks = 12;
    for (let i = 0; i < ticks; i++) {
      const time = now + (i * 0.09);
      // Low thud
      const thud = ctx.createOscillator();
      const thudGain = ctx.createGain();
      thud.type = 'triangle';
      thud.frequency.setValueAtTime(120 + Math.random() * 80, time);
      thud.frequency.exponentialRampToValueAtTime(50, time + 0.04);
      thudGain.gain.setValueAtTime(0.12, time);
      thudGain.gain.exponentialRampToValueAtTime(0.001, time + 0.04);
      thud.connect(thudGain);
      thudGain.connect(ctx.destination);
      thud.start(time);
      thud.stop(time + 0.05);

      // High metallic clack
      const click = ctx.createOscillator();
      const clickGain = ctx.createGain();
      click.type = 'sine';
      click.frequency.setValueAtTime(800 + Math.random() * 600, time + 0.01);
      clickGain.gain.setValueAtTime(0.06, time + 0.01);
      clickGain.gain.exponentialRampToValueAtTime(0.001, time + 0.03);
      click.connect(clickGain);
      clickGain.connect(ctx.destination);
      click.start(time + 0.01);
      click.stop(time + 0.035);
    }
  } catch {
    // Audio failure acceptable
  }
};

// Bright Chime when Ball drops into cradle
const playBallDropChime = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const notes = [587.33, 880.00]; // D5, A5
    notes.forEach((freq, idx) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.setValueAtTime(freq, now + (idx * 0.09));
      gain.gain.setValueAtTime(0.2, now + (idx * 0.09));
      gain.gain.exponentialRampToValueAtTime(0.001, now + (idx * 0.09) + 0.4);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + (idx * 0.09));
      osc.stop(now + (idx * 0.09) + 0.45);
    });

    // Landing thud
    const thud = ctx.createOscillator();
    const thudGain = ctx.createGain();
    thud.type = 'triangle';
    thud.frequency.setValueAtTime(140, now);
    thud.frequency.exponentialRampToValueAtTime(45, now + 0.08);
    thudGain.gain.setValueAtTime(0.18, now);
    thudGain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
    thud.connect(thudGain);
    thudGain.connect(ctx.destination);
    thud.start(now);
    thud.stop(now + 0.09);
  } catch {
    // Audio failure acceptable
  }
};

// Grand Victory Fanfare when BINGO is claimed
const playBingoVictory = (ctx: AudioContext) => {
  try {
    const now = ctx.currentTime;
    const fanfare = [
      { freq: 523.25, time: 0, dur: 0.16 }, // C5
      { freq: 659.25, time: 0.16, dur: 0.16 }, // E5
      { freq: 783.99, time: 0.32, dur: 0.22 }, // G5
      { freq: 1046.50, time: 0.54, dur: 0.8 }  // C6
    ];
    fanfare.forEach(({ freq, time, dur }) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'triangle';
      osc.frequency.setValueAtTime(freq, now + time);
      gain.gain.setValueAtTime(0.25, now + time);
      gain.gain.exponentialRampToValueAtTime(0.001, now + time + dur);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(now + time);
      osc.stop(now + time + dur + 0.05);
    });
  } catch {
    // Audio failure acceptable
  }
};

// Intelligently score and pick natural, non-robotic voices
const scoreVoice = (v: SpeechSynthesisVoice): number => {
  const name = v.name.toLowerCase();
  let s = 0;
  if (v.lang.startsWith('en')) s += 10;
  if (v.lang.includes('US') || v.lang.includes('GB')) s += 5;
  // Prioritize modern neural / natural voices
  if (name.includes('natural') || name.includes('online') || name.includes('neural')) s += 60;
  if (name.includes('jenny') || name.includes('guy') || name.includes('aria')) s += 45;
  if (name.includes('google')) s += 35;
  if (name.includes('samantha')) s += 30;
  if (name.includes('zira')) s += 25;
  // Strongly deprioritize old SAPI David / robotic desktop synth
  if (name.includes('david')) s -= 40;
  if (name.includes('desktop')) s -= 25;
  return s;
};

// All 75 numbers
const ALL_NUMBERS = Array.from({ length: 75 }, (_, i) => i + 1);

export function BingoCallerBoard() {
  const rootRef = useRef<HTMLDivElement>(null);
  const audioContextRef = useRef<AudioContext | null>(null);

  const [calledNumbers, setCalledNumbers] = useState<number[]>([]);
  // Audio Mode: 'full' (Voice + SFX), 'sfx-only' (Chimes/Tumble only), 'muted'
  const [audioMode, setAudioMode] = useState<'full' | 'sfx-only' | 'muted'>('full');
  const [isSassyEnabled, setIsSassyEnabled] = useState(true);
  const [isRolling, setIsRolling] = useState(false);
  const [currentSassyQuote, setCurrentSassyQuote] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [autoPace, setAutoPace] = useState<number>(0); // 0 = Manual, 6 = 6s, 9 = 9s, 12 = 12s, 15 = 15s
  const [countdownProgress, setCountdownProgress] = useState<number>(0);
  const [bingoClaimed, setBingoClaimed] = useState(false);

  // SpeechSynthesis voices
  const [voices, setVoices] = useState<SpeechSynthesisVoice[]>([]);
  const [selectedVoice, setSelectedVoice] = useState<string>('');

  useEffect(() => {
    if (!('speechSynthesis' in window)) return;
    const loadVoices = () => {
      const all = window.speechSynthesis.getVoices();
      const en = all.filter((v) => v.lang.startsWith('en'));
      const list = en.length > 0 ? en : all;
      // Sort highest quality / natural voices first
      const sorted = list.slice().sort((a, b) => scoreVoice(b) - scoreVoice(a));
      setVoices(sorted);
      if (sorted.length > 0 && !selectedVoice) {
        setSelectedVoice(sorted[0].name);
      }
    };
    loadVoices();
    window.speechSynthesis.onvoiceschanged = loadVoices;
    return () => {
      if ('speechSynthesis' in window) {
        window.speechSynthesis.onvoiceschanged = null;
      }
    };
  }, [selectedVoice]);

  // Web Audio Context getter
  const getAudio = useCallback(() => {
    if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
      audioContextRef.current = getAudioContext();
    }
    if (audioContextRef.current && audioContextRef.current.state === 'suspended') {
      audioContextRef.current.resume().catch(() => {});
    }
    return audioContextRef.current;
  }, []);

  // Fullscreen listener
  useEffect(() => {
    const onFullscreenChange = () => {
      setIsFullscreen(Boolean(document.fullscreenElement));
    };
    document.addEventListener('fullscreenchange', onFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', onFullscreenChange);
  }, []);

  const toggleFullscreen = useCallback(() => {
    if (!rootRef.current) return;
    if (!document.fullscreenElement) {
      rootRef.current.requestFullscreen().catch(() => {
        setIsFullscreen(true);
      });
    } else {
      document.exitFullscreen().catch(() => {});
    }
  }, []);

  // Derive uncalled numbers
  const uncalledNumbers = useMemo(() => {
    return ALL_NUMBERS.filter((n) => !calledNumbers.includes(n));
  }, [calledNumbers]);

  const lastCalled = calledNumbers[calledNumbers.length - 1] || null;
  const history = calledNumbers.slice(0, -1).reverse().slice(0, 7);

  // Announce Ball function with natural caller inflection
  const announceBall = useCallback((num: number, sassyText?: string) => {
    if (audioMode !== 'full' || !('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') {
      return;
    }
    
    window.speechSynthesis.cancel();
    
    const letter = getBingoLetter(num);
    // Human caller phrasing: "Under the I... 19! That's I, 19."
    const callText = `Under the ${letter}... ${num}! That's ${letter}, ${num}.`;
    const msg = new SpeechSynthesisUtterance(callText);
    msg.rate = 0.98; // Natural conversational tempo
    msg.pitch = 1.05; // Slightly elevated game-show host enthusiasm
    
    if (selectedVoice) {
      const v = voices.find((x) => x.name === selectedVoice);
      if (v) msg.voice = v;
    }

    if (sassyText && isSassyEnabled) {
      msg.onend = () => {
        // Short natural breath before sassy quip
        setTimeout(() => {
          if (!('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') return;
          const sassyMsg = new SpeechSynthesisUtterance(sassyText);
          sassyMsg.rate = 1.0;
          sassyMsg.pitch = 1.12;
          if (selectedVoice) {
            const v = voices.find((x) => x.name === selectedVoice);
            if (v) sassyMsg.voice = v;
          }
          window.speechSynthesis.speak(sassyMsg);
        }, 150);
      };
    }

    window.speechSynthesis.speak(msg);
  }, [audioMode, selectedVoice, voices, isSassyEnabled]);

  // Test voice preview button
  const handleTestVoice = () => {
    if (!('speechSynthesis' in window) || typeof SpeechSynthesisUtterance === 'undefined') return;
    window.speechSynthesis.cancel();
    const ctx = getAudio();
    if (ctx && audioMode !== 'muted') playBallDropChime(ctx);

    const testMsg = new SpeechSynthesisUtterance("Under the B... 4! Like I loved my spouse before I met you!");
    testMsg.rate = 1.0;
    testMsg.pitch = 1.08;
    if (selectedVoice) {
      const v = voices.find((x) => x.name === selectedVoice);
      if (v) testMsg.voice = v;
    }
    window.speechSynthesis.speak(testMsg);
  };

  // Draw Next Ball with rolling animation
  const drawNext = useCallback(() => {
    if (uncalledNumbers.length === 0 || isRolling) return;

    setIsRolling(true);
    setBingoClaimed(false);

    // Audio tumble sound
    if (audioMode !== 'muted') {
      const ctx = getAudio();
      if (ctx) playCageTumblerSound(ctx);
    }

    // Roll duration: 1100ms
    setTimeout(() => {
      const randomIndex = Math.floor(Math.random() * uncalledNumbers.length);
      const nextNum = uncalledNumbers[randomIndex];
      const quote = getSassyQuote(nextNum);

      setCalledNumbers((prev) => [...prev, nextNum]);
      setCurrentSassyQuote(quote);
      setIsRolling(false);

      if (audioMode !== 'muted') {
        const ctx = getAudio();
        if (ctx) playBallDropChime(ctx);
      }

      announceBall(nextNum, quote);
    }, 1100);
  }, [uncalledNumbers, isRolling, audioMode, getAudio, announceBall]);

  // Trigger BINGO claimed celebration
  const triggerBingoClaim = useCallback(() => {
    setBingoClaimed(true);
    setAutoPace(0);

    if (audioMode !== 'muted') {
      const ctx = getAudio();
      if (ctx) playBingoVictory(ctx);

      if (audioMode === 'full' && 'speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined') {
        window.speechSynthesis.cancel();
        const msg = new SpeechSynthesisUtterance("BINGO! We have a bingo claim! Please hold your cards while the caller verifies!");
        msg.rate = 1.0;
        msg.pitch = 1.15;
        if (selectedVoice) {
          const v = voices.find((x) => x.name === selectedVoice);
          if (v) msg.voice = v;
        }
        window.speechSynthesis.speak(msg);
      }
    }
  }, [audioMode, getAudio, selectedVoice, voices]);

  // Auto Pace Timer effect
  useEffect(() => {
    if (autoPace === 0 || uncalledNumbers.length === 0 || isRolling || bingoClaimed) {
      setCountdownProgress(0);
      return;
    }

    const intervalMs = 100;
    const totalMs = autoPace * 1000;
    let elapsed = 0;

    const timer = setInterval(() => {
      elapsed += intervalMs;
      setCountdownProgress(Math.min(100, (elapsed / totalMs) * 100));

      if (elapsed >= totalMs) {
        clearInterval(timer);
        setCountdownProgress(0);
        drawNext();
      }
    }, intervalMs);

    return () => clearInterval(timer);
  }, [autoPace, uncalledNumbers.length, isRolling, bingoClaimed, drawNext]);

  const resetBoard = () => {
    if (window.confirm('Are you sure you want to reset the board? All drawn balls will be cleared.')) {
      setCalledNumbers([]);
      setCurrentSassyQuote(null);
      setBingoClaimed(false);
      setAutoPace(0);
      if ('speechSynthesis' in window && typeof SpeechSynthesisUtterance !== 'undefined') {
        window.speechSynthesis.cancel();
      }
    }
  };

  // Keyboard Shortcuts (Space, F, M, S, B)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (
        e.target instanceof HTMLInputElement || 
        e.target instanceof HTMLTextAreaElement || 
        e.target instanceof HTMLSelectElement
      ) {
        return;
      }

      if (e.code === 'Space') {
        e.preventDefault();
        drawNext();
      } else if (e.key === 'f' || e.key === 'F') {
        e.preventDefault();
        toggleFullscreen();
      } else if (e.key === 'm' || e.key === 'M') {
        e.preventDefault();
        setAudioMode((prev) => (prev === 'full' ? 'sfx-only' : prev === 'sfx-only' ? 'muted' : 'full'));
      } else if (e.key === 's' || e.key === 'S') {
        e.preventDefault();
        setIsSassyEnabled((prev) => !prev);
      } else if (e.key === 'b' || e.key === 'B') {
        e.preventDefault();
        triggerBingoClaim();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [drawNext, toggleFullscreen, triggerBingoClaim]);

  // Columns for the master board
  const columns = [
    { letter: 'B' as const, range: [1, 15] },
    { letter: 'I' as const, range: [16, 30] },
    { letter: 'N' as const, range: [31, 45] },
    { letter: 'G' as const, range: [46, 60] },
    { letter: 'O' as const, range: [61, 75] }
  ];

  const currentTheme = lastCalled ? BALL_THEMES[getBingoLetter(lastCalled) || 'B'] : BALL_THEMES.B;

  return (
    <div 
      ref={rootRef}
      className={cn(
        "w-full transition-colors duration-300 bg-[#0B0D17] text-white",
        isFullscreen 
          ? "fixed inset-0 z-50 p-6 md:p-8 flex flex-col justify-between overflow-y-auto"
          : "max-w-6xl mx-auto space-y-8 p-4 md:p-6 rounded-3xl border-4 border-[#1E2338] shadow-2xl"
      )}
    >
      {/* Confetti Celebration Particles when BINGO is claimed */}
      {bingoClaimed && (
        <div className="fixed inset-0 pointer-events-none z-50 overflow-hidden" data-testid="bingo-confetti">
          {Array.from({ length: 48 }).map((_, i) => (
            <motion.div
              key={i}
              initial={{
                opacity: 1,
                x: '50vw',
                y: '35vh',
                scale: Math.random() * 0.9 + 0.6,
                rotate: 0
              }}
              animate={{
                opacity: [1, 1, 0],
                x: `${(Math.random() - 0.5) * 95 + 50}vw`,
                y: `${Math.random() * 95}vh`,
                rotate: Math.random() * 720 - 360
              }}
              transition={{
                duration: 2.8 + Math.random(),
                ease: "easeOut"
              }}
              className={cn(
                "absolute w-4 h-4 rounded-sm shadow-lg",
                ['bg-[#FFD600]', 'bg-[#0044FF]', 'bg-[#FF0055]', 'bg-[#00CC66]', 'bg-purple-500', 'bg-amber-400', 'bg-cyan-400'][i % 7]
              )}
            />
          ))}
        </div>
      )}

      {/* Control Bar: Fullscreen, Sound Mode, Sassy Toggle, Voice Selector */}
      <div className="flex flex-wrap items-center justify-between gap-4 p-4 rounded-2xl bg-[#131626] border-2 border-[#242A42] shadow-xl">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 bg-[#FFD600] text-black px-3.5 py-1.5 font-black uppercase text-xs tracking-wider border-2 border-black rounded-lg shadow-[2px_2px_0px_#000]">
            <Sparkles className="w-4 h-4 text-black" />
            <span>Grand Bingo Hall</span>
          </div>
          <span className="text-xs font-bold text-slate-400 hidden xl:inline">
            Shortcuts: Space (Draw), F (Fullscreen), S (Sassy), M (Sound), B (Bingo!)
          </span>
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          {/* Sassy Caller Toggle */}
          <button
            type="button"
            onClick={() => setIsSassyEnabled(!isSassyEnabled)}
            className={cn(
              "flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-black uppercase text-xs tracking-wider transition-all cursor-pointer border-2",
              isSassyEnabled 
                ? "bg-[#FFD600] text-black border-black shadow-[3px_3px_0px_#000]" 
                : "bg-[#1C2136] text-slate-400 border-[#2E3654] hover:text-white"
            )}
            title="Toggle quirky, sassy caller quips for each number (S)"
          >
            <Flame className={cn("w-4 h-4", isSassyEnabled ? "text-rose-600 fill-rose-600" : "text-slate-500")} />
            <span>Sassy Quips {isSassyEnabled ? 'ON' : 'OFF'}</span>
          </button>

          {/* Sound Mode Cycle Toggle */}
          <button
            type="button"
            onClick={() => setAudioMode((prev) => (prev === 'full' ? 'sfx-only' : prev === 'sfx-only' ? 'muted' : 'full'))}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-black uppercase text-xs bg-[#1C2136] hover:bg-[#252C48] text-white border-2 border-[#2E3654] cursor-pointer transition-colors"
            title="Cycle audio: Voice + Chimes -> Chimes Only -> Mute (M)"
          >
            {audioMode === 'full' && <Volume2 className="w-4 h-4 text-emerald-400" />}
            {audioMode === 'sfx-only' && <Volume1 className="w-4 h-4 text-amber-400" />}
            {audioMode === 'muted' && <VolumeX className="w-4 h-4 text-rose-400" />}
            <span>
              {audioMode === 'full' ? 'Voice & SFX' : audioMode === 'sfx-only' ? 'Chimes Only' : 'Muted'}
            </span>
          </button>

          {/* Voice Selector & Test Button */}
          {voices.length > 0 && (
            <div className="flex items-center gap-1 bg-[#1C2136] border-2 border-[#2E3654] rounded-xl px-2 py-1">
              <select
                value={selectedVoice}
                onChange={(e) => setSelectedVoice(e.target.value)}
                className="text-xs font-bold bg-transparent text-white focus:outline-none max-w-[140px] sm:max-w-[170px] truncate cursor-pointer py-1"
                title="Select announcer voice"
              >
                {voices.map((v) => (
                  <option key={v.name} value={v.name} className="bg-[#131626] text-white">
                    {v.name.replace(/(Microsoft|Google|Apple)\s*/g, '')}
                  </option>
                ))}
              </select>

              <button
                type="button"
                onClick={handleTestVoice}
                className="text-[10px] font-black uppercase px-2 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded cursor-pointer"
                title="Preview voice pronunciation"
              >
                Test
              </button>
            </div>
          )}

          {/* Fullscreen Button */}
          <button
            type="button"
            onClick={toggleFullscreen}
            className="flex items-center gap-1.5 px-3.5 py-2 rounded-xl font-black uppercase text-xs bg-white text-black hover:bg-yellow-400 hover:text-black transition-colors cursor-pointer border-2 border-white shadow-[2px_2px_0px_rgba(255,255,255,0.2)]"
            title={isFullscreen ? 'Exit Fullscreen (F)' : 'Enter Projector / Big Screen Mode (F)'}
          >
            {isFullscreen ? <Minimize className="w-4 h-4" /> : <Maximize className="w-4 h-4" />}
            <span>{isFullscreen ? 'Exit Full' : 'Full Screen'}</span>
          </button>
        </div>
      </div>

      {/* Bingo Claimed Notification Banner */}
      <AnimatePresence>
        {bingoClaimed && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: -20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            className="bg-gradient-to-r from-yellow-400 via-amber-300 to-yellow-500 text-black border-4 border-black p-5 rounded-3xl shadow-[0_0_40px_rgba(250,204,21,0.8)] flex flex-col sm:flex-row items-center justify-between gap-4"
          >
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-black text-yellow-400 flex items-center justify-center rounded-2xl border-2 border-black shadow-md">
                <Trophy className="w-8 h-8" />
              </div>
              <div>
                <h3 className="text-2xl font-black uppercase tracking-tight font-display">
                  🎉 BINGO CLAIMED! HOLD YOUR CARDS!
                </h3>
                <p className="text-sm font-bold text-black/85">
                  Card verification in progress. Click &lsquo;Resume Game&rsquo; once verified.
                </p>
              </div>
            </div>
            <button
              onClick={() => setBingoClaimed(false)}
              className="px-6 py-3 bg-black text-white font-black uppercase tracking-wider text-xs border-2 border-black rounded-xl shadow-[4px_4px_0px_#FFF] hover:bg-blue-600 transition-all cursor-pointer"
            >
              Resume Game
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Top Action Stage: Caller Cage, 3D Ball & Controls */}
      <div className="grid lg:grid-cols-12 gap-6 items-center p-6 md:p-8 rounded-3xl bg-[#121524] border-2 border-[#232942] shadow-2xl">
        {/* Left Column: Action Controls, Auto-Timer & Progress (4 Cols) */}
        <div className="lg:col-span-4 flex flex-col gap-4">
          <Button 
            onClick={drawNext} 
            disabled={uncalledNumbers.length === 0 || isRolling}
            className="w-full h-18 text-xl font-black uppercase tracking-wider bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white border-2 border-blue-400 shadow-[0_0_25px_rgba(59,130,246,0.5)] active:scale-98 transition-all cursor-pointer"
          >
            <Play className={cn("w-6 h-6 mr-2 fill-white", isRolling && "animate-spin")} />
            {isRolling ? 'Spinning Cage...' : 'Draw Next Ball'}
          </Button>

          {/* Quick Action Buttons: Bingo Claim + Reset */}
          <div className="grid grid-cols-2 gap-3">
            <button
              type="button"
              onClick={triggerBingoClaim}
              className="py-3.5 px-3 bg-[#FFD600] text-black border-2 border-black font-black uppercase text-xs tracking-wider shadow-[3px_3px_0px_#000] hover:bg-[#FFE552] rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <Trophy className="w-4 h-4 text-black" />
              <span>BINGO! 🎉</span>
            </button>

            <button
              type="button"
              onClick={resetBoard}
              className="py-3.5 px-3 bg-[#241A22] text-rose-300 border-2 border-rose-500/40 hover:border-rose-400 font-black uppercase text-xs tracking-wider rounded-xl transition-all flex items-center justify-center gap-2 cursor-pointer"
            >
              <RotateCcw className="w-4 h-4 text-rose-400" />
              <span>Reset Game</span>
            </button>
          </div>

          {/* Auto-Draw Pace Selector & Timer */}
          <div className="p-3.5 bg-[#171C30] border-2 border-[#262E4A] rounded-2xl space-y-2.5">
            <div className="flex items-center justify-between text-xs font-black uppercase tracking-wider text-slate-300">
              <span className="flex items-center gap-1.5 text-blue-400">
                <Clock className="w-4 h-4" /> Auto-Caller Pace
              </span>
              <span className="text-[11px] text-slate-400 font-mono">
                {autoPace > 0 ? `Every ${autoPace}s` : 'Manual'}
              </span>
            </div>

            <div className="grid grid-cols-5 gap-1.5 text-[11px] font-black">
              {[0, 6, 9, 12, 15].map((pace) => (
                <button
                  key={pace}
                  onClick={() => setAutoPace(pace)}
                  className={cn(
                    "py-1.5 rounded-lg border uppercase transition-all cursor-pointer font-black",
                    autoPace === pace
                      ? "bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-blue-400 shadow-[0_0_12px_rgba(59,130,246,0.6)]"
                      : "bg-[#111424] text-slate-400 border-[#252C46] hover:text-white hover:border-slate-500"
                  )}
                >
                  {pace === 0 ? 'Off' : `${pace}s`}
                </button>
              ))}
            </div>

            {/* Countdown bar when auto-pace is active */}
            {autoPace > 0 && (
              <div className="w-full bg-[#111424] h-2.5 rounded-full overflow-hidden border border-[#252C46]">
                <div 
                  className="bg-gradient-to-r from-blue-500 to-cyan-400 h-full transition-all duration-100" 
                  style={{ width: `${countdownProgress}%` }}
                />
              </div>
            )}
          </div>

          {/* Balls Counter */}
          <div className="text-center text-xs font-mono font-black uppercase tracking-widest text-slate-400">
            {75 - uncalledNumbers.length} CALLED • {uncalledNumbers.length} REMAINING
          </div>
        </div>

        {/* Center Column: Golden Tumbler Cage, 3D Ball & Sassy Commentary (5 Cols) */}
        <div className="lg:col-span-5 flex flex-col items-center justify-center min-h-[320px]">
          <span className="text-xs font-mono font-black uppercase tracking-widest text-slate-400 mb-3">
            {isRolling ? 'Tumbling Wire Cage' : 'Current Ball'}
          </span>

          {/* Visual Ball Chute & 3D Sphere */}
          <div className="relative flex flex-col items-center justify-center">
            {isRolling ? (
              /* Golden Tumbler Cage Animation with Bouncing Balls */
              <div className="relative w-52 h-52 sm:w-60 sm:h-60 flex items-center justify-center">
                <div className="relative w-48 h-48 sm:w-56 sm:h-56 rounded-full border-4 border-[#F59E0B] shadow-[0_0_45px_rgba(245,158,11,0.45)] bg-gradient-to-b from-[#1E1B15] to-[#0D0F17] flex items-center justify-center overflow-hidden">
                  {/* Rotating wire slats */}
                  <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ repeat: Infinity, duration: 0.65, ease: "linear" }}
                    className="absolute inset-0 rounded-full border-4 border-dashed border-[#FBBF24]/70"
                  />
                  <motion.div
                    animate={{ rotate: -360 }}
                    transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
                    className="absolute inset-2 rounded-full border-2 border-dotted border-[#F59E0B]/50"
                  />
                  {/* Central Spindle */}
                  <div className="absolute w-5 h-5 rounded-full bg-gradient-to-tr from-amber-600 to-yellow-300 border-2 border-black z-20 shadow-md" />
                  <div className="absolute w-full h-1 bg-amber-500/70 z-10" />

                  {/* Tumbling Mini Balls inside the cage */}
                  {[
                    { color: 'from-blue-500 to-indigo-700', letter: 'B' },
                    { color: 'from-rose-500 to-red-700', letter: 'I' },
                    { color: 'from-amber-400 to-orange-600', letter: 'N' },
                    { color: 'from-emerald-500 to-teal-700', letter: 'G' },
                    { color: 'from-purple-500 to-indigo-800', letter: 'O' }
                  ].map((b, i) => (
                    <motion.div
                      key={i}
                      animate={{
                        x: [Math.sin(i * 1.5) * 45, Math.cos(i * 1.2) * 45, -Math.sin(i * 1.5) * 45, Math.sin(i * 1.5) * 45],
                        y: [-35 + (i * 16), 30 - (i * 14), -15, -35 + (i * 16)],
                        rotate: [0, 180, 360, 540]
                      }}
                      transition={{ repeat: Infinity, duration: 0.6 + i * 0.08, ease: "easeInOut" }}
                      className={cn(
                        "absolute w-7 h-7 rounded-full bg-gradient-to-br shadow-md flex items-center justify-center text-[10px] font-black text-white border border-white/50",
                        b.color
                      )}
                    >
                      {b.letter}
                    </motion.div>
                  ))}
                </div>
                <span className="absolute -bottom-2 px-3 py-1 bg-amber-400 text-black font-black uppercase text-[10px] tracking-widest rounded-full shadow-lg border border-black animate-pulse z-30">
                  ⚡ Spinning Cage...
                </span>
              </div>
            ) : lastCalled ? (
              /* High-Pizzazz 3D Ball with Spherical Lighting */
              <motion.div
                key={lastCalled}
                initial={{ opacity: 0, scale: 0.2, y: -90, rotate: -540 }}
                animate={{ opacity: 1, scale: 1, y: 0, rotate: 0 }}
                transition={{ type: "spring", stiffness: 220, damping: 18 }}
                className={cn(
                  "relative w-52 h-52 sm:w-60 sm:h-60 rounded-full p-2.5 flex items-center justify-center border-4 border-black/50 shadow-2xl",
                  currentTheme.gradient,
                  currentTheme.shadow
                )}
                style={{
                  background: `radial-gradient(circle at 35% 25%, #ffffff 0%, var(--tw-gradient-stops) 45%, #05060a 100%)`
                }}
              >
                {/* 3D Specular Gloss Highlight Arch */}
                <div className="absolute top-3 left-6 w-20 h-10 bg-gradient-to-b from-white/70 via-white/30 to-transparent rounded-full blur-[1px] transform -rotate-30 pointer-events-none" />

                {/* Inner Ball Badge */}
                <div className="w-34 h-34 sm:w-40 sm:h-40 rounded-full bg-gradient-to-b from-white to-slate-200 border-4 border-black/30 flex flex-col items-center justify-center shadow-[inset_0_4px_12px_rgba(0,0,0,0.35)]">
                  <span className={cn("text-2xl sm:text-3xl font-black leading-none uppercase tracking-tighter drop-shadow-sm", currentTheme.text)}>
                    {currentTheme.letter}
                  </span>
                  <span className="text-6xl sm:text-7xl font-black text-slate-950 leading-none tracking-tight font-display">
                    {lastCalled}
                  </span>
                </div>
              </motion.div>
            ) : (
              /* Ready State */
              <div className="w-52 h-52 sm:w-60 sm:h-60 rounded-full border-4 border-dashed border-[#2A324E] flex flex-col items-center justify-center text-slate-400 bg-[#121626]">
                <Sparkles className="w-10 h-10 mb-2 text-yellow-400/70" />
                <span className="font-black uppercase tracking-widest text-sm text-slate-300">Ready to Call</span>
              </div>
            )}

            {/* Ball Ground Shadow */}
            <div className="w-40 h-4 bg-black/60 rounded-full blur-md mt-4" />
          </div>

          {/* Quirky / Sassy Caller Speech Bubble */}
          <AnimatePresence mode="wait">
            {isSassyEnabled && currentSassyQuote && !isRolling && (
              <motion.div
                key={currentSassyQuote}
                initial={{ opacity: 0, y: 14, scale: 0.92 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -10, scale: 0.92 }}
                transition={{ type: "spring", stiffness: 280, damping: 22 }}
                className="mt-5 px-6 py-4 bg-gradient-to-r from-[#FFD600] to-[#FFC400] text-black border-[3px] border-black rounded-3xl shadow-[6px_6px_0px_#000] max-w-sm sm:max-w-md text-center relative"
              >
                {/* Speech bubble pointer */}
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 w-0 h-0 border-x-8 border-x-transparent border-b-8 border-b-black" />
                <div className="absolute -top-2 left-1/2 -translate-x-1/2 w-0 h-0 border-x-7 border-x-transparent border-b-7 border-b-[#FFD600]" />

                <div className="flex items-center justify-center gap-1.5 text-xs font-black uppercase tracking-wider text-black/80 mb-1">
                  <Flame className="w-4 h-4 text-rose-600 fill-rose-600" />
                  <span>Sassy Caller Quip</span>
                </div>
                <p className="text-base sm:text-lg font-black italic leading-snug font-display">
                  &ldquo;{currentSassyQuote}&rdquo;
                </p>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Right Column: Previous Balls History Rack (3 Cols) */}
        <div className="lg:col-span-3 flex flex-col h-full bg-[#141829] rounded-3xl p-4 border-2 border-[#242A42] shadow-inner">
          <h2 className="text-xs font-mono font-black uppercase tracking-wider text-slate-400 mb-3 flex items-center justify-between">
            <span className="flex items-center gap-1.5 text-blue-400">
              <SkipForward className="w-4 h-4" /> Previous Balls
            </span>
            <span className="text-[11px] px-2 py-0.5 bg-[#1E243D] text-slate-300 rounded font-bold">
              {history.length}
            </span>
          </h2>

          <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-2 gap-2.5">
            <AnimatePresence mode="popLayout">
              {history.length > 0 ? (
                history.map((num, i) => {
                  const letter = getBingoLetter(num);
                  const theme = BALL_THEMES[letter || 'B'];
                  return (
                    <motion.div
                      key={num}
                      layout
                      initial={{ opacity: 0, scale: 0.5, y: -10 }}
                      animate={{ opacity: 1, scale: 1, y: 0 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      transition={{ type: "spring", stiffness: 300, damping: 25 }}
                      className={cn(
                        "flex items-center justify-center py-2 px-3 rounded-2xl font-black text-sm border-2 gap-1.5 shadow-md",
                        i === 0 
                          ? "bg-[#252B46] text-white border-yellow-400 shadow-[0_0_12px_rgba(250,204,21,0.4)]" 
                          : "bg-[#1A1F35] text-slate-300 border-[#2C3454]"
                      )}
                    >
                      <span className={cn("text-xs font-black", theme.text)}>{letter}</span>
                      <span className="text-base font-black font-display">{num}</span>
                    </motion.div>
                  );
                })
              ) : (
                <div className="col-span-full py-10 text-center text-xs font-bold text-slate-400 italic">
                  Balls drawn will appear here
                </div>
              )}
            </AnimatePresence>
          </div>
        </div>
      </div>

      {/* Master Board Grid (Crystal Clear High Contrast in Dark Mode) */}
      <div className="rounded-3xl bg-[#121524] border-2 border-[#232942] shadow-2xl p-6 md:p-8 overflow-x-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-black uppercase tracking-tight font-display text-white">
              Master Board
            </h2>
            <span className="text-xs font-mono font-bold px-2.5 py-1 bg-[#1F253D] text-yellow-400 border border-[#2F375A] rounded-lg">
              75 BALLS
            </span>
          </div>

          <div className="flex items-center gap-5 text-xs font-bold text-slate-300">
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-yellow-400 ring-2 ring-yellow-200 shadow-[0_0_8px_rgba(250,204,21,0.8)]" />
              <span>Last Called</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3.5 h-3.5 rounded bg-blue-600 ring-2 ring-blue-400 shadow-[0_0_8px_rgba(59,130,246,0.8)]" />
              <span>Drawn</span>
            </div>
          </div>
        </div>
        
        <div className="min-w-[680px] flex flex-col gap-3.5">
          {columns.map((col) => {
            const [start, end] = col.range;
            const numbers = Array.from({ length: 15 }, (_, i) => start + i);
            const theme = BALL_THEMES[col.letter];
            
            return (
              <div key={col.letter} className="flex gap-3.5 items-center">
                {/* Column Letter Header Badge */}
                <div className={cn(
                  "w-14 h-14 flex items-center justify-center font-black text-3xl rounded-2xl",
                  theme.badgeBg
                )}>
                  {col.letter}
                </div>
                
                {/* 15 Number Tiles */}
                <div className="flex-1 grid gap-2" style={{ gridTemplateColumns: 'repeat(15, minmax(0, 1fr))' }}>
                  {numbers.map((num) => {
                    const isCalled = calledNumbers.includes(num);
                    const isLast = lastCalled === num;
                    
                    return (
                      <div
                        key={num}
                        data-testid={`master-ball-${num}`}
                        className={cn(
                          "w-full aspect-square flex items-center justify-center rounded-xl font-black text-sm md:text-base transition-all duration-300 select-none",
                          isLast 
                            ? theme.lastCalledBg
                            : isCalled 
                              ? theme.calledBg
                              : "bg-[#181D30] text-[#A2B0D0] border border-[#262D4A] hover:border-slate-400 hover:text-white"
                        )}
                      >
                        {num}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
